import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

/**
 * Reusable scroll animations.
 *
 * HTML (any element):
 *   custom-anim="fade-up"           fade | fade-up | fade-up-soft | fade-up-big | fade-down | fade-left | fade-right | scale-in | draw-line
 *   custom-anim-delay="0.15"        seconds
 *   custom-anim-duration="0.85"     seconds
 *   custom-anim-stagger="0.1"       stagger children
 *   custom-anim-children="p"        child selector (defaults to :scope > *)
 *   custom-anim-start="top 82%"     ScrollTrigger start
 *   custom-anim-ease="power3.out"
 *
 * data-animate is the same API (used on the landing page).
 *
 * Line-decors are picked up automatically. Add line-decor--reverse to draw
 * right-to-left (dot appears on the left). Vertical lines draw upward then
 * show the top dot.
 */

const EASE = "power3.out";
const START = "top 82%";
const PLAYED = "animatePlayed";

const PRESETS = {
  fade: { from: { autoAlpha: 0 }, duration: 0.8 },
  "fade-up": { from: { autoAlpha: 0, y: 28 }, duration: 0.85 },
  "fade-up-soft": { from: { autoAlpha: 0, y: 16 }, duration: 0.7 },
  "fade-up-big": { from: { autoAlpha: 0, y: 48 }, duration: 1.05 },
  "fade-down": { from: { autoAlpha: 0, y: -24 }, duration: 0.85 },
  "fade-left": { from: { autoAlpha: 0, x: -56 }, duration: 1 },
  "fade-right": { from: { autoAlpha: 0, x: 56 }, duration: 1 },
  "scale-in": { from: { autoAlpha: 0, scale: 0.94 }, duration: 0.9 },
};

const prefersReduced = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const markPlayed = (els) => {
  els.forEach((el) => {
    if (el) el.dataset[PLAYED] = "true";
  });
};

const isPlayed = (el) => el?.dataset?.[PLAYED] === "true";

const toVars = (preset, duration, ease) => {
  const to = { autoAlpha: 1, duration, ease, overwrite: "auto" };
  if ("x" in (preset.from || {})) to.x = 0;
  if ("y" in (preset.from || {})) to.y = 0;
  if ("scale" in (preset.from || {})) to.scale = 1;
  return to;
};

const playDrawLine = (el, { delay = 0, start = START } = {}) => {
  if (!el || isPlayed(el)) return;
  markPlayed([el]);

  const reveal = () => el.classList.add("is-animated");

  ScrollTrigger.create({
    trigger: el,
    start,
    once: true,
    onEnter: () => {
      if (delay) gsap.delayedCall(delay, reveal);
      else reveal();
    },
  });
};

export function play(targets, type = "fade-up", options = {}) {
  const els = gsap.utils.toArray(targets).filter(Boolean);
  if (!els.length) return;

  if (type === "draw-line") {
    els.forEach((el) => playDrawLine(el, options));
    return;
  }

  const preset = PRESETS[type] || PRESETS["fade-up"];
  const duration = Number(options.duration ?? preset.duration);
  const delay = Number(options.delay ?? 0);
  const ease = options.ease || EASE;
  const stagger = options.stagger != null ? Number(options.stagger) : 0;
  const start = options.start || START;
  const trigger = options.trigger || els[0];

  const fresh = els.filter((el) => !isPlayed(el));
  if (!fresh.length) return;
  markPlayed(fresh);

  gsap.fromTo(
    fresh,
    { ...preset.from },
    {
      ...toVars(preset, duration, ease),
      delay,
      stagger,
      scrollTrigger: {
        trigger,
        start,
        once: true,
      },
    },
  );
}

const bindAttr = (attr) => {
  document.querySelectorAll(`[${attr}]`).forEach((el) => {
    if (isPlayed(el)) return;
    const type = el.getAttribute(attr) || "fade-up";
    const childrenSel = el.getAttribute(`${attr}-children`);
    const stagger = el.getAttribute(`${attr}-stagger`);
    const opts = {
      delay: el.getAttribute(`${attr}-delay`),
      duration: el.getAttribute(`${attr}-duration`),
      ease: el.getAttribute(`${attr}-ease`) || undefined,
      start: el.getAttribute(`${attr}-start`) || undefined,
      trigger: el,
    };

    if (stagger != null && stagger !== "") {
      const kids = childrenSel
        ? [...el.querySelectorAll(childrenSel)]
        : [...el.children];
      if (kids.length) {
        play(kids, type, { ...opts, stagger: Number(stagger) });
        markPlayed([el]);
        return;
      }
    }

    play(el, type, opts);
  });
};

const bindDeclared = () => {
  bindAttr("custom-anim");
  bindAttr("data-animate");
};

const scene = (trigger, build) => {
  if (!trigger) return;
  const tl = gsap.timeline({
    defaults: { ease: EASE, overwrite: "auto" },
    scrollTrigger: {
      trigger,
      start:
        trigger.matches(".hero, .error") || document.querySelector(".error")
          ? "top 95%"
          : START,
      once: true,
    },
  });
  build(tl, trigger);
};

const addLine = (tl, el, at = 0) => {
  if (!el || isPlayed(el)) return;
  markPlayed([el]);
  tl.call(() => el.classList.add("is-animated"), null, at);
};

const tween = (tl, targets, type, at = 0, extra = {}) => {
  const els = gsap.utils.toArray(targets).filter((el) => el && !isPlayed(el));
  if (!els.length) return;
  markPlayed(els);
  const preset = PRESETS[type] || PRESETS["fade-up"];
  const duration = extra.duration ?? preset.duration;
  tl.fromTo(
    els,
    { ...preset.from },
    {
      ...toVars(preset, duration, extra.ease || EASE),
      stagger: extra.stagger || 0,
      immediateRender: true,
    },
    at,
  );
};

const bindScenes = () => {
  const hero = document.querySelector(".hero");
  scene(hero, (tl) => {
    tween(tl, hero.querySelector(".hero__panel"), "fade", 0, { duration: 0.9 });
    tween(tl, hero.querySelectorAll(".hero__orb"), "fade-left", 0.06, {
      duration: 1.15,
    });
    const titleLines = hero.querySelectorAll(".hero__title-line");
    if (titleLines.length)
      tween(tl, titleLines, "fade-up", 0.2, { stagger: 0.12, duration: 0.8 });
    else tween(tl, hero.querySelector(".hero__title"), "fade-up", 0.2);
    tween(tl, hero.querySelector(".hero__subtitle"), "fade-up", 0.42, {
      duration: 0.7,
    });
  });

  const intro = document.querySelector(".intro");
  scene(intro, (tl) => {
    addLine(tl, intro.querySelector(".line-decor"), 0);
    tween(tl, intro.querySelector(".intro__heading"), "fade-up", 0.12);
    tween(tl, intro.querySelectorAll(".intro__body p"), "fade-up", 0.22, {
      stagger: 0.14,
    });
    tween(tl, intro.querySelector(".intro__diagram"), "fade-left", 0.28, {
      duration: 1,
    });
    tween(tl, intro.querySelector(".intro__diagram"), "fade-up", 0.4, {
      duration: 1,
    });
    tween(tl, intro.querySelector(".intro__blob"), "fade", 0.2, {
      duration: 1.2,
    });
  });

  const tech = document.querySelector(".technology");
  scene(tech, (tl) => {
    addLine(tl, tech.querySelector(".line-decor"), 0);
    tween(tl, tech.querySelector(".technology__heading"), "fade-up", 0.1);
    tween(tl, tech.querySelector(".technology__sub"), "fade-up", 0.2);
    tween(
      tl,
      tech.querySelectorAll(".accordion .accordion-item, .accordion details"),
      "fade-up",
      0.28,
      {
        stagger: 0.08,
        duration: 0.7,
      },
    );
    tween(tl, tech.querySelector(".technology__powder"), "fade-left", 0.12, {
      duration: 1.15,
    });
    tween(
      tl,
      tech.querySelector(".technology__particles"),
      "fade-right",
      0.24,
      { duration: 1.1 },
    );
    tween(
      tl,
      tech.querySelector(".technology-side-content"),
      "fade-right",
      0.24,
      { duration: 1.1 },
    );
    const captions = tech.querySelectorAll(".technology-side-content h4");
    if (captions.length) {
      markPlayed([...captions]);
      tl.call(
        () => captions.forEach((el) => el.classList.add("is-animated")),
        null,
        0.48,
      );
    }
  });

  const specialties = document.querySelector(".specialties");
  scene(specialties, (tl) => {
    addLine(tl, specialties.querySelector(":scope > .line-decor"), 0);
    const topLine = [...specialties.querySelectorAll(".line-decor")].find(
      (el) => !el.closest(".specialty-panel"),
    );
    if (topLine) addLine(tl, topLine, 0);
    tween(
      tl,
      specialties.querySelector(".specialties__heading"),
      "fade-up",
      0.1,
    );
    tween(tl, specialties.querySelector(".specialties__sub"), "fade-up", 0.2);
    tween(
      tl,
      specialties.querySelectorAll(".specialty-card"),
      "scale-in",
      0.22,
      { stagger: 0.12, duration: 0.9 },
    );
    const lemonPanel = specialties.querySelector(".specialty-panel--lemon");
    addLine(tl, lemonPanel?.querySelector(".line-decor"), 0.28);
    addLine(
      tl,
      specialties.querySelector(".specialty-panel--beta .line-decor"),
      0.28,
    );
    tween(
      tl,
      lemonPanel?.querySelectorAll(".specialty-panel__stat"),
      "fade-up",
      0.34,
      { stagger: 0.1 },
    );
  });

  const cta = document.querySelector(".cta");
  scene(cta, (tl) => {
    addLine(tl, cta.querySelector(".line-decor"), 0);
    tween(tl, cta.querySelector(".cta__heading"), "fade-up", 0.08);
    tween(
      tl,
      cta.querySelectorAll(".cta__body p, .cta__tagline"),
      "fade-up",
      0.18,
      { stagger: 0.12 },
    );
    tween(tl, cta.querySelector(".contact-form"), "fade-up", 0.16, {
      duration: 0.95,
    });
  });

  const error = document.querySelector(".error");
  scene(error, (tl) => {
    addLine(tl, error.querySelector(".line-decor"), 0);
    tween(tl, error.querySelector(".error__code"), "fade-up", 0.08);
    tween(tl, error.querySelector(".error__title"), "fade-up", 0.18);
    tween(tl, error.querySelector(".error__body"), "fade-up", 0.28);
    tween(tl, error.querySelector(".submit-btn"), "fade-up", 0.36);
  });

  const footer = document.querySelector(".footer");
  scene(footer, (tl) => {
    tween(tl, footer.querySelector(".footer__logo"), "fade-up", 0, {
      duration: 0.9,
    });
    tween(tl, footer.querySelector(".footer__contact"), "fade-up", 0.1);
    tween(tl, footer.querySelector(".footer__visit"), "fade-up", 0.18);
    tween(tl, footer.querySelector(".footer__copyright"), "fade", 0.28, {
      duration: 0.7,
    });
  });
};

const bindRemainingLines = () => {
  document.querySelectorAll(".line-decor").forEach((el) => {
    if (isPlayed(el)) return;
    playDrawLine(el, { start: "top 90%" });
  });
};

export function initAnimate() {
  try {
    if (Number(window.ambidryTheme?.isBuilder) === 1) {
      return;
    }

    if (prefersReduced()) {
      document
        .querySelectorAll(".line-decor, .technology-side-content h4")
        .forEach((el) => {
          el.classList.add("is-animated");
        });
      return;
    }

    if (document.body.classList.contains("custom-landing-page")) {
      bindScenes();
      bindRemainingLines();
    }

    bindDeclared();

    requestAnimationFrame(() => ScrollTrigger.refresh());
    window.addEventListener("load", () => ScrollTrigger.refresh());
  } catch (error) {
    document.documentElement.classList.remove("has-js");
    document
      .querySelectorAll(".line-decor")
      .forEach((el) => el.classList.add("is-animated"));
    console.error(error);
  }
}
